'use client'

import { useEffect } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useAuth } from './AuthProvider'
import { isSignOutNavigationUnderway } from '../../api-client/sign-out-navigation'
import SessionSplash from '../session/SessionSplash'
import ErrorState from '../ui/ErrorState'

interface AuthGuardProps {
  children: React.ReactNode
}

/**
 * Gates dashboard surfaces on the resolved session state.
 *
 * BUG-1182: redirects to sign-in ONLY on a confirmed `'unauthenticated'`. A
 * transient `'error'` (a 5xx / network blip on the session read — the auth-hot-path
 * `staff_users` read blips at Better Auth's 60s cookieCache refresh) renders a
 * retryable state instead of bouncing a still-valid operator mid-session. The
 * pre-fix guard redirected on any `!user`, so a transient failure logged people
 * out (incl. a clerk off a live auction).
 *
 * BL-1109: the session is now resolved server-side and streamed into
 * `SessionProvider`, which suspends until it lands — so inside a provider
 * `loading` is already `false` on the first render and the `<Suspense>` fallback
 * in the layout owns the splash. The `loading` branch below still fires for the
 * outside-a-provider fallback (`useAuth`'s `OUTSIDE_PROVIDER_FALLBACK`, used in
 * static generation and isolated tests), where treating "no provider" as "signed
 * out" would be wrong.
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter()
  const pathname = usePathname()
  const { loading, authStatus, refreshUser } = useAuth()

  useEffect(() => {
    // Redirect ONLY on a real, confirmed logout — never on a transient error.
    // A sign-out this device started is already loading sign-in; a second navigation would
    // cancel that load (BUG-2233).
    if (!loading && authStatus === 'unauthenticated' && !isSignOutNavigationUnderway()) {
      const returnUrl = encodeURIComponent(pathname)
      router.replace(`/auth/sign-in?returnUrl=${returnUrl}`)
    }
  }, [loading, authStatus, router, pathname])

  if (loading) {
    return <SessionSplash />
  }

  // Transient failure resolving the session (5xx / network) — offer a retry
  // instead of a mid-session logout (BUG-1182). `refreshUser` re-reads
  // /api/auth/me; a real 401 there flips authStatus to 'unauthenticated' and the
  // effect above redirects, while a recovered session mounts the app.
  if (authStatus === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <ErrorState
          message="We couldn't verify your session. Check your connection and try again."
          onRetry={refreshUser}
        />
      </div>
    )
  }

  if (authStatus === 'unauthenticated') {
    return null
  }

  return <>{children}</>
}
